import { useState, MouseEvent } from "react"
import { Button, Menu, MenuItem, ListSubheader } from "@mui/material"
import { Compare, ArrowDropDown, InfoOutlined } from "@mui/icons-material"
import {
  useIsEndVsStartDateComparisonEnabled,
  useIsDashboardOptimizeExperimentsFeatureEnabled,
  useIsDashboardSelectionSizingFeatureEnabled,
  useIsDashboardEqualIdioWeightingFeatureEnabled,
} from "resources/account"
import OPTooltip from "components/material/tooltip"
import { RuleWeightingStrategyType } from "helpers/types/instruments/selected"
import { OPTIMIZATION_STRATEGY_LABEL } from "connected-components/optimize-experiments"
import { useIntradayState } from "../hooks/use-intraday-state"
import "./comparison-menu.scss"

export default function ComparisonMenu({
  onInstrumentClick,
  onDateComparisonClick,
  setIsOptimizePanelExpanded,
  onSetWeightStrategy,
}: ComparisonMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const isEndVsStartDateComparisonEnabled = useIsEndVsStartDateComparisonEnabled()
  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isSelectionSizingEnabled = useIsDashboardSelectionSizingFeatureEnabled()
  const isEqualIdioWeightingEnabled = useIsDashboardEqualIdioWeightingFeatureEnabled()
  const [{ isIntradayMode }] = useIntradayState()

  function handleToggle(event: MouseEvent<HTMLButtonElement>) {
    if (isEndVsStartDateComparisonEnabled || isOptimizeExperimentsEnabled || isSelectionSizingEnabled || isEqualIdioWeightingEnabled) {
      setAnchor(event.currentTarget)
      setIsOpen(prev => !prev)
    } else {
      onInstrumentClick()
    }
  }

  function handleClose() {
    setIsOpen(false)
  }

  return (
    <>
      <Button
        variant="text"
        onClick={handleToggle}
        className="search-comparison-wrapper"
        startIcon={<Compare />}
      >
        <div className="search-comparison-label-container">
          <span>Compare</span>
          {(isEndVsStartDateComparisonEnabled || isOptimizeExperimentsEnabled || isSelectionSizingEnabled || isEqualIdioWeightingEnabled) && <ArrowDropDown />}
        </div>
      </Button>
      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={handleClose}
        disablePortal
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={() => {
          onInstrumentClick()
          handleClose()
        }}
        >
          Instrument
        </MenuItem>
        <MenuItem onClick={() => {
          onDateComparisonClick()
          handleClose()
        }}
        >
          End vs start date
        </MenuItem>
        {isOptimizeExperimentsEnabled && (
          isIntradayMode ? (
            <OPTooltip title="Optimization strategy is not available for intraday data">
              <span>
                <MenuItem disabled={true}>
                {OPTIMIZATION_STRATEGY_LABEL}
                </MenuItem>
              </span>
            </OPTooltip>
            ) : (
            <MenuItem
              onClick={() => {
                setIsOptimizePanelExpanded(true)
                handleClose()
              }}
            >
              {OPTIMIZATION_STRATEGY_LABEL}
            </MenuItem>
          )
        )}
        {(isSelectionSizingEnabled || isEqualIdioWeightingEnabled) && [
          <ListSubheader key="strategy-selection-header">Decision Analysis</ListSubheader>,
          isSelectionSizingEnabled && (
            <MenuItem
              key="equal-weight"
              className="dashboard-comparison-menu-strategy-item"
              onClick={() => {
                onSetWeightStrategy("equalWeighting")
                handleClose()
              }}
            >
              <div className="dashboard-comparison-menu-strategy-item-content">
                <span>Equal-Sized</span>
                <OPTooltip title="Compare to an equal-weighted portfolio to show alpha gained or lost from sizing decisions.">
                  <InfoOutlined className="dashboard-comparison-menu-info-icon" />
                </OPTooltip>
              </div>
            </MenuItem>
          ),
          isEqualIdioWeightingEnabled && (
            <MenuItem
              key="idio-weight"
              className="dashboard-comparison-menu-strategy-item"
              onClick={() => {
                onSetWeightStrategy("idioWeighting")
                handleClose()
              }}
            >
              <div className="dashboard-comparison-menu-strategy-item-content">
                <span>Equal Idio-Sized</span>
                <OPTooltip title="Compares your portfolio where each position contributes equally to idiosyncratic risk — helps identify where idio risk is concentrated or well-balanced.">
                  <InfoOutlined className="dashboard-comparison-menu-info-icon" />
                </OPTooltip>
              </div>
            </MenuItem>
          ),
        ].filter(Boolean)}
      </Menu>
    </>
  )
}

interface ComparisonMenuProps {
  onInstrumentClick: () => void
  onDateComparisonClick: () => void
  isOptimizePanelExpanded: boolean
  setIsOptimizePanelExpanded: (isOptimizePanelExpanded: boolean) => void
  onSetWeightStrategy: (weightStrategy: RuleWeightingStrategyType) => void
}
